import React from 'react';
import type { Device } from '../types';
import { SECURITY_INFO } from '../constants';

interface DevicesSidebarProps {
  devices: Device[];
  onTerminateDevice: (deviceId: string) => void;
  onTerminateAllSessions: () => void;
  loading?: boolean;
}

const DevicesSidebar: React.FC<DevicesSidebarProps> = ({
  devices,
  onTerminateDevice,
  onTerminateAllSessions,
  loading = false,
}) => {
  const currentDevice = devices.find((d) => d.isCurrent);
  const otherDevices = devices.filter((d) => !d.isCurrent);

  return (
    <div className="flex flex-col gap-4">
      {/* Devices Card */}
      <div className="bg-white rounded-2xl p-4 flex flex-col items-center gap-4">
        <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
          <path d="M56 12H16C12.69 12 10 14.69 10 18V42C10 45.31 12.69 48 16 48H30V54H42V48H56C59.31 48 62 45.31 62 42V18C62 14.69 59.31 12 56 12ZM56 42H16V18H56V42Z" fill="#0B111E"/>
        </svg>

        <h3 style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '20px', lineHeight: '27px', color: '#282828', textAlign: 'center' }}>
          Devices
        </h3>

        {currentDevice && (
          <>
            <p style={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '18px', lineHeight: '25px', color: '#282828' }}>
              This device
            </p>
            <div className="flex items-center gap-1">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 18C20.55 18 21 17.55 21 17V7C21 6.45 20.55 6 20 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H0V20H24V18H20ZM5 8H19V16H5V8Z" fill="#282828"/>
              </svg>
              <span style={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '14px', color: '#353535' }}>
                {currentDevice.name}
              </span>
            </div>
          </>
        )}

        <div className="w-full h-px bg-[#F4F4F4]" />

        {otherDevices.length > 0 && (
          <>
            <button
              onClick={onTerminateAllSessions}
              disabled={loading}
              className="flex items-center gap-1 text-[#DC0000] hover:opacity-70 transition-opacity disabled:opacity-50"
              style={{ fontFamily: 'Manrope', fontWeight: 400, fontSize: '16px', lineHeight: '26px' }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M23 5.5V18.5C23 19.6 22.1 20.5 21 20.5H14V18.5H21V7.5H3V9.5H1V5.5C1 4.4 1.9 3.5 3 3.5H21C22.1 3.5 23 4.4 23 5.5ZM15 12H17L13.5 15.5L10 12H12V8H15V12ZM2 14H4V12H8V14H10L6.5 17.5L3 14H2Z" stroke="#DC0000" strokeWidth="1.5"/>
              </svg>
              Terminate All Other Sessions
            </button>

            <div className="w-full h-px bg-[#F4F4F4]" />

            <p style={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '18px', lineHeight: '25px', color: '#282828', alignSelf: 'flex-start' }}>
              Active Devices
            </p>

            {otherDevices.map((device) => (
              <div key={device.id} className="w-full flex items-start gap-4 py-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 1H7C5.9 1 5 1.9 5 3V21C5 22.1 5.9 23 7 23H17C18.1 23 19 22.1 19 21V3C19 1.9 18.1 1 17 1ZM17 19H7V5H17V19Z" fill="#282828"/>
                </svg>
                <div className="flex-1">
                  <p style={{ fontFamily: 'Manrope', fontWeight: 500, fontSize: '14px', lineHeight: '22px', color: '#0B111E' }}>
                    {device.browser} {device.name}
                  </p>
                  <p style={{ fontFamily: 'Manrope', fontWeight: 400, fontSize: '10px', lineHeight: '14px', color: '#757575' }}>
                    {device.location}. {device.lastActive}
                  </p>
                </div>
                <button
                  onClick={() => onTerminateDevice(device.id)}
                  disabled={loading}
                  className="hover:opacity-70 transition-opacity disabled:opacity-50"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#282828"/>
                  </svg>
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Security Info Cards */}
      <div className="bg-white rounded-2xl p-4 flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <div className="w-14 h-14 bg-[#EFF5FF] rounded-full flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M16 4C9.38 4 4 9.38 4 16C4 22.62 9.38 28 16 28C22.62 28 28 22.62 28 16C28 9.38 22.62 4 16 4ZM16 10C17.1 10 18 10.9 18 12C18 13.1 17.1 14 16 14C14.9 14 14 13.1 14 12C14 10.9 14.9 10 16 10ZM19 22H13V20H15V17H14V15H17V20H19V22Z" fill="#015FA6"/>
            </svg>
          </div>
          <div>
            <h4 style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '16px', lineHeight: '22px', color: '#0B111E', marginBottom: '8px' }}>
              {SECURITY_INFO.WHY_HIDDEN.title}
            </h4>
            <p style={{ fontFamily: 'Manrope', fontWeight: 400, fontSize: '12px', lineHeight: '16px', color: '#757575' }}>
              {SECURITY_INFO.WHY_HIDDEN.description}
            </p>
          </div>
        </div>

        <div className="w-full h-px bg-[#EDEDED]" />

        <div className="flex flex-col gap-3">
          <div className="w-14 h-14 bg-[#EFF5FF] rounded-full flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M24 10H22V8C22 4.69 19.31 2 16 2C12.69 2 10 4.69 10 8V10H8C6.9 10 6 10.9 6 12V26C6 27.1 6.9 28 8 28H24C25.1 28 26 27.1 26 26V12C26 10.9 25.1 10 24 10ZM16 22C14.9 22 14 21.1 14 20C14 18.9 14.9 18 16 18C17.1 18 18 18.9 18 20C18 21.1 17.1 22 16 22ZM20 10H12V8C12 5.79 13.79 4 16 4C18.21 4 20 5.79 20 8V10Z" fill="#015FA6"/>
            </svg>
          </div>
          <div>
            <h4 style={{ fontFamily: 'Manrope', fontWeight: 600, fontSize: '16px', lineHeight: '22px', color: '#0B111E', marginBottom: '8px' }}>
              {SECURITY_INFO.EDITABLE_DETAILS.title}
            </h4>
            <p style={{ fontFamily: 'Manrope', fontWeight: 400, fontSize: '12px', lineHeight: '16px', color: '#757575' }}>
              {SECURITY_INFO.EDITABLE_DETAILS.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevicesSidebar;
